import { listDispositionsForWorkflow } from '@100mslive/disposition-engine';
import type {
  FindCaseRecordsRequest,
  FindOrganizationCaseRecordsRequest,
} from '@compass/client/types/case_record';
import { CaseExportView } from '@compass/client/types/case_record';
import type { GetSummaryResponse } from '@compass/client/types/summary';

import {
  AEPQC_PREPROCESSING_WORKFLOW_TYPE,
  DISPOSITION_WORKFLOW_TYPES,
  WorkflowType,
} from '@/constants/dispositions';
import { EXPORT_BATCH_SIZE, EXPORT_PAGE_SIZE, MAX_EXPORT_RECORDS } from '@/constants/export';
import { foldWorkflowSlaMap, foldWorkflowVersionMap } from '@/hooks/useWorkflowSlaMap';
import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';
import {
  callService,
  caseRecordService,
  schemaService,
  summaryService,
  userService,
  workflowService,
} from '@/services/api';
import { eligibilityChecksService } from '@/services/eligibilityChecks.service';
import type {
  CaseExportData,
  ExportConfig,
  ExportFormat,
  ExportProgress,
  ExportRequest,
  OrgExportColumnsMode,
  UIFieldInfo,
  UISchema,
  UISchemaGroup,
} from '@/types/export.types';
import { buildCampaignRow, flattenSchemaFields } from '@/utils/campaignFields';
import { exportData, prepareExportConfig, sanitizeFilenamePart } from '@/utils/export';
import { parseFieldGroups } from '@/utils/formSchema';

interface ProgramContext {
  organizationId: string;
  programId: string;
}

/**
 * Extracts field information from UI schema
 */
/**
 * Flattens one schema field into its export columns. Nested array/object fields
 * (`drug`, `procedure`) contribute their *leaves* under a dotted id
 * (`drug.code`) — a container has no cell of its own, and exporting it whole
 * rendered as `[object Object]`. Mirrors campaignFields.flattenSchemaFields so
 * both export paths agree on the column set; repeated leaves are `|`-joined at
 * row build time.
 */
const flattenFieldToColumns = (
  field: {
    id: string;
    label?: string;
    type?: string;
    options?: UIFieldInfo['options'];
    fields?: unknown;
  },
  groupLabel: string,
  prefix = '',
): UIFieldInfo[] => {
  const id = prefix ? `${prefix}.${field.id}` : field.id;
  const nested = Array.isArray(field.fields) ? (field.fields as (typeof field)[]) : [];

  if (nested.length > 0) {
    return nested.flatMap(child => flattenFieldToColumns(child, groupLabel, id));
  }

  return [
    {
      id,
      label: field.label || field.id,
      groupLabel,
      type: field.type,
      options: field.options,
    },
  ];
};

const extractFieldsFromUISchema = (schema: UISchema | undefined): UIFieldInfo[] => {
  const fields: UIFieldInfo[] = [];

  if (!schema?.groups) {
    return fields;
  }

  schema.groups.forEach((group: UISchemaGroup) => {
    const groupLabel = group.label || '';

    group.fields?.forEach(field => {
      fields.push(...flattenFieldToColumns(field, groupLabel));
    });
  });

  return fields;
};

/**
 * Determines unique output groups from field information
 */
const getUniqueOutputGroups = (outputFields: UIFieldInfo[]): string[] => {
  const groups = new Set<string>();

  outputFields.forEach(field => {
    if (field.groupLabel) {
      groups.add(field.groupLabel);
    }
  });

  return Array.from(groups).sort();
};

/**
 * Fetches case records for a single workflow with cursor-based pagination
 */
const fetchCaseRecordsForWorkflow = async (
  context: ProgramContext,
  workflowId: string | undefined,
  filters?: Omit<FindCaseRecordsRequest, 'limit' | 'start_after_id'>,
): Promise<CaseExportData[]> => {
  const records: CaseExportData[] = [];
  let startAfterId: string | undefined = undefined;
  let hasMore = true;

  while (hasMore) {
    // Use the reduced /caserecords/export projection (view=EXPORT): same filters
    // and cursor pagination as the search endpoint, but the response drops
    // output_data.metadata / input_data.metadata / schema_version / extracted_data,
    // which the export never reads — only the fixed columns + input_data.data /
    // output_data.data leaf values are used downstream.
    const response = await caseRecordService.exportCaseRecords({
      organization_id: context.organizationId,
      program_id: context.programId,
      workflow_id: workflowId,
      ...filters,
      view: CaseExportView.CASE_EXPORT_VIEW_EXPORT,
      limit: EXPORT_PAGE_SIZE,
      start_after_id: startAfterId,
    });

    const pageRecords: CaseExportData[] = response.case_records || [];
    records.push(...pageRecords);
    hasMore = pageRecords.length === EXPORT_PAGE_SIZE;
    if (hasMore && pageRecords.length > 0) {
      startAfterId = pageRecords[pageRecords.length - 1].id;
    }
  }

  return records;
};

/**
 * Fetches case records by program with cursor-based pagination support
 * If workflow_id is provided in filters, only fetches from that workflow
 * Otherwise fetches ALL case records across ALL workflows in the program
 */
const fetchCaseRecordsByProgram = async (
  context: ProgramContext,
  filters?: Omit<FindCaseRecordsRequest, 'limit' | 'start_after_id'>,
): Promise<CaseExportData[]> => {
  // If workflow_id is provided, only fetch from that workflow
  if (filters?.workflow_id) {
    return fetchCaseRecordsForWorkflow(context, filters.workflow_id, filters);
  }

  // Original behavior: fetch from all workflows if no workflow_id
  const workflowsResponse = await workflowService.getWorkflowsByProgram({
    organization_id: context.organizationId,
    program_id: context.programId,
  });
  const workflows = workflowsResponse.workflows || [];

  if (workflows.length === 0) {
    return [];
  }

  // Fetch case records for each workflow using cursor-based pagination
  const allRecords: CaseExportData[] = [];
  for (const workflow of workflows) {
    const workflowRecords = await fetchCaseRecordsForWorkflow(context, workflow.id, filters);
    allRecords.push(...workflowRecords);
  }

  return allRecords;
};

/**
 * Fetches calls for case records
 */
const fetchCallsForCases = async (
  context: ProgramContext,
  caseRecords: CaseExportData[],
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  let processed = 0;
  const total = caseRecords.length;

  // Process in batches to avoid overwhelming the API
  const batchSize = EXPORT_BATCH_SIZE;
  for (let i = 0; i < caseRecords.length; i += batchSize) {
    const batch = caseRecords.slice(i, Math.min(i + batchSize, caseRecords.length));

    onProgress?.({
      status: 'fetching',
      current: processed,
      total,
      message: `Fetching calls (${processed}/${total} cases processed)...`,
    });

    await Promise.all(
      batch.map(async caseRecord => {
        try {
          if (!caseRecord.id) return; // Skip if no id
          const callsResponse = await callService.getCaseRecordCalls({
            organization_id: context.organizationId,
            program_id: context.programId,
            caserecord_id: caseRecord.id,
          });

          // Use calls directly as they already match CallInfo
          caseRecord.calls = callsResponse.calls || [];
        } catch (error) {
          console.warn(`Failed to fetch calls for case ${caseRecord.id}:`, error);
          caseRecord.calls = [];
        }
      }),
    );

    processed += batch.length;
  }

  onProgress?.({
    status: 'fetching',
    current: processed,
    total,
    message: 'Calls fetched successfully',
  });
};

/**
 * Fetches eligibility checks for case records
 */
const fetchEligibilityChecksForCases = async (
  {
    caseRecords,
    organizationId,
    programId,
  }: {
    caseRecords: CaseExportData[];
    organizationId: string;
    programId: string;
  },
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  let processed = 0;
  const total = caseRecords.length;

  // Process in batches to avoid overwhelming the API
  const batchSize = EXPORT_BATCH_SIZE;
  for (let i = 0; i < caseRecords.length; i += batchSize) {
    const batch = caseRecords.slice(i, Math.min(i + batchSize, caseRecords.length));

    onProgress?.({
      status: 'fetching',
      current: processed,
      total,
      message: `Fetching eligibility checks (${processed}/${total} cases processed)...`,
    });

    await Promise.all(
      batch.map(async caseRecord => {
        try {
          if (!caseRecord.id) return; // Skip if no id
          const eligibilityResponse = await eligibilityChecksService.getCaseRecordEligibilityChecks(
            {
              organization_id: organizationId,
              program_id: programId,
              caserecord_id: caseRecord.id,
              // Exclude skipped eligibility checks from the export
              statuses: ['created', 'in_progress', 'completed', 'failed', 'cancelled'],
            },
          );

          // Use eligibility checks directly (already filtered by status)
          caseRecord.eligibility_checks = eligibilityResponse.eligibility_checks || [];
        } catch (error) {
          console.warn(`Failed to fetch eligibility checks for case ${caseRecord.id}:`, error);
          caseRecord.eligibility_checks = [];
        }
      }),
    );

    processed += batch.length;
  }

  onProgress?.({
    status: 'fetching',
    current: processed,
    total,
    message: 'Eligibility checks fetched successfully',
  });
};

/**
 * Fetches summaries for case records
 */
const fetchSummariesForCases = async (
  context: ProgramContext,
  caseRecords: CaseExportData[],
  onProgress?: (progress: ExportProgress) => void,
): Promise<Map<string, GetSummaryResponse>> => {
  const summariesMap = new Map<string, GetSummaryResponse>();
  let processed = 0;
  const total = caseRecords.length;

  // Process in batches
  const batchSize = EXPORT_BATCH_SIZE;
  for (let i = 0; i < caseRecords.length; i += batchSize) {
    const batch = caseRecords.slice(i, Math.min(i + batchSize, caseRecords.length));

    onProgress?.({
      status: 'fetching',
      current: processed,
      total,
      message: `Fetching summaries (${processed}/${total} cases processed)...`,
    });

    await Promise.all(
      batch.map(async caseRecord => {
        try {
          if (!caseRecord.id) return; // Skip if no id
          const summaryResponse = await summaryService.getCaseSummary({
            organization_id: context.organizationId,
            program_id: context.programId,
            case_id: caseRecord.id,
          });

          if (summaryResponse?.summary?.content?.section_summaries) {
            summariesMap.set(caseRecord.id, summaryResponse);
          }
        } catch (error) {
          // Summaries are optional, so we can continue if they fail
          console.warn(`Failed to fetch summary for case ${caseRecord.id}:`, error);
        }
      }),
    );

    processed += batch.length;
  }

  onProgress?.({
    status: 'fetching',
    current: processed,
    total,
    message: 'Summaries fetched successfully',
  });

  return summariesMap;
};

/**
 * Fetches and processes schema for a single workflow
 * Uses schema service instead of workflow.ui to support BSR fallback
 */
const fetchAndProcessWorkflowSchema = async (
  context: ProgramContext,
  workflowId: string,
  fieldsMap: Map<string, UIFieldInfo>,
  allInputFields: UIFieldInfo[],
  allOutputFields: UIFieldInfo[],
  checkDuplicates: boolean = false,
): Promise<void> => {
  try {
    // Use schema service instead of workflow.ui (supports BSR fallback)
    const schemaResponse = await schemaService.getSchema({
      organization_id: context.organizationId,
      program_id: context.programId,
      workflow_id: workflowId,
    });

    const inputFields = extractFieldsFromUISchema(schemaResponse?.input as UISchema);
    const outputFields = extractFieldsFromUISchema(schemaResponse?.output as UISchema);

    inputFields.forEach(field => {
      const key = `input_${field.id}`;
      if (!checkDuplicates || !fieldsMap.has(key)) {
        fieldsMap.set(key, field);
        allInputFields.push(field);
      }
    });

    outputFields.forEach(field => {
      const key = `output_${field.id}`;
      if (!checkDuplicates || !fieldsMap.has(key)) {
        fieldsMap.set(key, field);
        allOutputFields.push(field);
      }
    });
  } catch (error) {
    console.warn(`Failed to fetch schema for workflow ${workflowId}:`, error);
  }
};

/**
 * Fetches and merges schemas from workflows in a program
 * If workflowId is provided, only fetches schema for that specific workflow
 * Otherwise fetches and merges schemas from all workflows
 */
const fetchProgramSchemas = async (
  context: ProgramContext,
  workflowId?: string,
): Promise<{
  inputFields: UIFieldInfo[];
  outputFields: UIFieldInfo[];
  outputGroups: string[];
}> => {
  const allInputFields: UIFieldInfo[] = [];
  const allOutputFields: UIFieldInfo[] = [];
  const fieldsMap = new Map<string, UIFieldInfo>();

  // If workflowId is provided, only fetch schema for that workflow
  if (workflowId) {
    await fetchAndProcessWorkflowSchema(
      context,
      workflowId,
      fieldsMap,
      allInputFields,
      allOutputFields,
      false,
    );
    const outputGroups = getUniqueOutputGroups(allOutputFields);
    return { inputFields: allInputFields, outputFields: allOutputFields, outputGroups };
  }

  // Get all workflows for the program to fetch their schemas
  const workflowsResponse = await workflowService.getWorkflowsByProgram({
    organization_id: context.organizationId,
    program_id: context.programId,
  });
  const workflows = workflowsResponse.workflows || [];

  // Fetch schemas for all workflows
  for (const workflow of workflows) {
    if (!workflow.id) continue;
    await fetchAndProcessWorkflowSchema(
      context,
      workflow.id,
      fieldsMap,
      allInputFields,
      allOutputFields,
      true,
    );
  }

  const outputGroups = getUniqueOutputGroups(allOutputFields);

  return {
    inputFields: allInputFields,
    outputFields: allOutputFields,
    outputGroups,
  };
};

/**
 * Fetches disposition labels for all workflows in the program
 * Returns a map of disposition code → label
 */
const fetchDispositionsMap = async (
  context: ProgramContext,
  workflowId?: string,
): Promise<Map<number, string>> => {
  const dispositionsMap = new Map<number, string>();

  try {
    // Get workflows to discover which workflow_types are in scope for this
    // export. Post-BI-864 the disposition catalog is keyed on workflow_type
    // (globally-unique codes; same labels across all agents of a workflow_type),
    // so we no longer fan out one fetch per agent_id.
    const workflowsResponse = await workflowService.getWorkflowsByProgram({
      organization_id: context.organizationId,
      program_id: context.programId,
    });
    const workflows = workflowsResponse.workflows || [];

    const targetWorkflows = workflowId ? workflows.filter(w => w.id === workflowId) : workflows;

    // Only include workflow types that carry dispositions (PBM, UNIFIEDMEDBV, …)
    const dispositionWorkflowTypes = [
      ...new Set(
        targetWorkflows
          .map(w => w.workflow_type)
          .filter((wt): wt is string => !!wt && DISPOSITION_WORKFLOW_TYPES.includes(wt)),
      ),
    ];

    if (dispositionWorkflowTypes.length === 0) {
      return dispositionsMap;
    }

    for (const workflowType of dispositionWorkflowTypes) {
      try {
        const catalog = listDispositionsForWorkflow(resolveEngineWorkflowType(workflowType));
        for (const [code, label] of Object.entries(catalog)) {
          dispositionsMap.set(Number(code), label);
        }
      } catch (err) {
        console.warn(`Failed to load dispositions for workflow_type ${workflowType}:`, err);
      }
    }
  } catch (err) {
    console.warn('Failed to fetch dispositions:', err);
  }

  return dispositionsMap;
};

/**
 * Fetches the per-workflow SLA data the export's SLA Flag / SLA Timer columns
 * need (BV-1846): the SLA budget in minutes and the workflow version (which
 * decides how "breach soon" is detected).
 *
 * Both live on the workflow rather than the case, so they are keyed by
 * workflow_id. Reuses the same pure folds the case-list timer uses, so the
 * export and the table agree on every value.
 */
interface WorkflowSlaMaps {
  includeSla: boolean;
  slaByWorkflowId: Map<string, number>;
  versionByWorkflowId: Map<string, string>;
}

const EMPTY_SLA_MAPS: WorkflowSlaMaps = {
  includeSla: false,
  slaByWorkflowId: new Map(),
  versionByWorkflowId: new Map(),
};

const fetchWorkflowSlaMaps = async (
  context: ProgramContext,
  workflowType: string | undefined,
  workflowId?: string,
): Promise<WorkflowSlaMaps> => {
  // The SLA columns are limited to the payor workflow types. The case list
  // always downloads with a workflow selected, so the requested workflow_type
  // decides this for the whole file.
  if (!workflowType || !DISPOSITION_WORKFLOW_TYPES.includes(workflowType)) {
    return EMPTY_SLA_MAPS;
  }

  try {
    const response = await workflowService.getWorkflowsByProgram({
      organization_id: context.organizationId,
      program_id: context.programId,
    });
    const workflows = (response.workflows || []).filter(w => !workflowId || w.id === workflowId);
    const results = [{ data: { workflows } }];
    return {
      includeSla: true,
      slaByWorkflowId: foldWorkflowSlaMap(results),
      versionByWorkflowId: foldWorkflowVersionMap(results),
    };
  } catch (err) {
    console.warn('Failed to fetch workflow SLA data for export:', err);
    return EMPTY_SLA_MAPS;
  }
};

/**
 * Fetches user emails for created_by user IDs
 */
const fetchUserEmails = async (caseRecords: CaseExportData[]): Promise<Map<string, string>> => {
  const userIds = [
    ...new Set(caseRecords.map(c => c.created_by).filter((id): id is string => !!id)),
  ];

  if (userIds.length === 0) {
    return new Map();
  }

  try {
    const response = await userService.lookupUsers({ user_ids: userIds });
    const emailMap = new Map<string, string>();
    response.users?.forEach(user => {
      if (user.id && user.email) {
        emailMap.set(user.id, user.email);
      }
    });
    return emailMap;
  } catch (error) {
    console.warn('Failed to fetch user emails for export:', error);
    return new Map();
  }
};

/**
 * Processes the export and generates the file
 */
const processAndGenerateExport = async (
  caseRecords: CaseExportData[],
  schemas: {
    inputFields: UIFieldInfo[];
    outputFields: UIFieldInfo[];
    outputGroups: string[];
  },
  summariesMap: Map<string, GetSummaryResponse>,
  options: ExportRequest['options'],
  onProgress?: (progress: ExportProgress) => void,
  userEmailMap?: Map<string, string>,
  dispositionsMap?: Map<number, string>,
  includeSla: boolean = false,
  slaByWorkflowId?: Map<string, number>,
  versionByWorkflowId?: Map<string, string>,
): Promise<void> => {
  // Process and generate export
  onProgress?.({
    status: 'processing',
    current: 0,
    total: caseRecords.length,
    message: 'Processing data for export...',
  });

  const exportConfig: ExportConfig = prepareExportConfig(
    caseRecords,
    schemas.inputFields,
    schemas.outputFields,
    schemas.outputGroups,
    summariesMap,
    options.includeEligibilityChecks,
    options.callsOption || 'none',
    userEmailMap,
    dispositionsMap,
    options.workflowType === 'pqc',
    includeSla,
    slaByWorkflowId,
    versionByWorkflowId,
  );

  // Generate and download file
  onProgress?.({
    status: 'generating',
    current: caseRecords.length,
    total: caseRecords.length,
    message: `Generating ${options.format.toUpperCase()} file...`,
  });

  exportData(exportConfig, options);

  // Complete
  onProgress?.({
    status: 'complete',
    current: caseRecords.length,
    total: caseRecords.length,
    message: 'Export completed successfully',
  });
};

/**
 * Helper: Fetch summaries conditionally based on the export options.
 * Skipped for calls-only exports (no case rows to attach summaries to) and for
 * PQC workflows (PQC cases have no summaries, so the per-case fetch fan-out
 * would be wasted calls — the summary columns still render, empty, as before).
 */
const fetchSummariesConditionally = async (
  context: ProgramContext,
  caseRecords: CaseExportData[],
  options: ExportRequest['options'],
  onProgress?: (progress: ExportProgress) => void,
): Promise<Map<string, GetSummaryResponse>> => {
  if (options.callsOption === 'only' || options.workflowType === WorkflowType.PQC) {
    return new Map();
  }

  onProgress?.({
    status: 'fetching',
    current: 0,
    total: caseRecords.length,
    message: 'Fetching summaries...',
  });

  return await fetchSummariesForCases(context, caseRecords, onProgress);
};

/**
 * Helper: Fetch additional data (calls and eligibility checks)
 */
const fetchAdditionalData = async (
  context: ProgramContext,
  caseRecords: CaseExportData[],
  options: ExportRequest['options'],
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  // Fetch calls if requested
  const shouldFetchCalls = options.callsOption === 'include' || options.callsOption === 'only';
  if (shouldFetchCalls) {
    await fetchCallsForCases(context, caseRecords, onProgress);
  }

  // Fetch eligibility checks if requested
  if (options.includeEligibilityChecks) {
    await fetchEligibilityChecksForCases(
      {
        caseRecords,
        organizationId: context.organizationId,
        programId: context.programId,
      },
      onProgress,
    );
  }
};

/**
 * Main export service function
 */

export const exportCaseRecords = async (
  request: ExportRequest & { organizationId: string; programId: string },
): Promise<void> => {
  try {
    // Update progress
    request.onProgress?.({
      status: 'fetching',
      current: 0,
      total: 0,
      message: 'Starting export...',
    });

    const context: ProgramContext = {
      organizationId: request.organizationId,
      programId: request.programId,
    };

    // Fetch case records by program
    request.onProgress?.({
      status: 'fetching',
      current: 0,
      total: 0,
      message: 'Fetching case records...',
    });

    const caseRecords = await fetchCaseRecordsByProgram(context, request.filters);

    if (caseRecords.length === 0) {
      throw new Error('No case records found with the specified filters');
    }

    // Fetch schemas (from specific workflow if provided, otherwise all workflows)
    const schemas = await fetchProgramSchemas(context, request.filters?.workflow_id);

    // Fetch additional data (calls and eligibility checks)
    await fetchAdditionalData(context, caseRecords, request.options, request.onProgress);

    // Fetch summaries, user emails, dispositions, and SLA data concurrently
    const [summariesMap, userEmailMap, dispositionsMap, slaMaps] = await Promise.all([
      fetchSummariesConditionally(context, caseRecords, request.options, request.onProgress),
      fetchUserEmails(caseRecords),
      fetchDispositionsMap(context, request.filters?.workflow_id),
      fetchWorkflowSlaMaps(context, request.options.workflowType, request.filters?.workflow_id),
    ]);

    // Process and generate the export
    await processAndGenerateExport(
      caseRecords,
      schemas,
      summariesMap,
      request.options,
      request.onProgress,
      userEmailMap,
      dispositionsMap,
      slaMaps.includeSla,
      slaMaps.slaByWorkflowId,
      slaMaps.versionByWorkflowId,
    );
  } catch (error) {
    request.onProgress?.({
      status: 'error',
      current: 0,
      total: 0,
      message: error instanceof Error ? error.message : 'Export failed',
    });
    throw error;
  }
};

// ── Org-wide (Tier-2) export ───────────────────────────────────────────────────
// The per-program export above is scoped to a single (organization, program) and
// paginates FindCaseRecords. The Tier-2 PQC page is cross-program and uses the
// org-wide FindOrganizationCaseRecords endpoint, so it needs its own fetch loop.
// The columns are deliberately minimal: the fixed case columns plus ONLY the
// create-case (input) schema fields — no output fields, summaries, calls,
// eligibility checks, or dispositions.

/** Filters for the org-wide export (the page's request minus pagination). */
type OrgExportFilters = Omit<
  FindOrganizationCaseRecordsRequest,
  'organization_id' | 'limit' | 'start_after_id'
>;

/** Input field types that don't serialize to a flat spreadsheet cell. */
const NON_FLAT_FIELD_TYPES = new Set(['file', 'array', 'object']);

// Fields the org export must always include even if the aepqc_preprocessing schema
// doesn't define them: case_type (input) and the capture-at-create ack_id (output).
const CASE_TYPE_FIELD_ID = 'case_type';
const ACK_ID_FIELD_ID = 'ack_id';

export interface ExportOrgCaseRecordsRequest {
  organizationId: string;
  filters: OrgExportFilters;
  options: ExportRequest['options'];
  onProgress?: (progress: ExportProgress) => void;
  /** workflow_id → manufacturer (PQC workflow) name, for the Manufacturer column / filenames. */
  workflowNameById?: Map<string, string>;
  /**
   * Column source (default 'common'): the shared intake columns in one file, or
   * 'all' — one file per manufacturer, each with its workflow's full schema.
   */
  columnsMode?: OrgExportColumnsMode;
}

/** Outcome of an org-wide export, for caller-side toasts. */
export interface OrgExportResult {
  /** Number of files downloaded. */
  files: number;
  /** Manufacturers whose schema couldn't be loaded ('all' mode) — no file produced. */
  failedManufacturers: string[];
}

/**
 * Thrown by the 'all' (per-manufacturer) mode when no file could be produced
 * because every manufacturer's schema failed to load. The message is user-safe
 * (manufacturer names only — never case data).
 */
export class OrgExportSchemaError extends Error {}

/**
 * Inserts a "Manufacturer" column (each case's PQC workflow name, resolved from its
 * workflow_id) right after the ID column. The org export builds exactly one row per
 * case in order (no calls/eligibility rows), so rows and caseRecords line up 1:1.
 * No-op when that invariant doesn't hold or no name map is provided.
 */
const withManufacturerColumn = (
  config: ExportConfig,
  caseRecords: CaseExportData[],
  workflowNameById?: Map<string, string>,
): ExportConfig => {
  if (!workflowNameById || config.rows.length !== caseRecords.length) {
    return config;
  }
  const insertAt = 1; // right after the ID column
  const headers = [...config.headers];
  headers.splice(insertAt, 0, { name: 'manufacturer', label: 'Manufacturer', type: 'string' });
  const rows = config.rows.map((row, i) => {
    const name = workflowNameById.get(caseRecords[i]?.workflow_id ?? '') ?? null;
    const next = [...row];
    next.splice(insertAt, 0, name);
    return next;
  });
  return { headers, rows };
};

/**
 * Fetches every org-wide case record matching `filters`, across all pages.
 *
 * Mirrors `useOrgCases` pagination: the standard find path cursors by the last
 * record's id; the Atlas advanced path (free-text / output_conditions / advanced
 * sort) returns a keyset `next_page_token`. Prefer the token when present, else the
 * last id. Any partial page terminates the loop (works for both paths). Capped at
 * `MAX_EXPORT_RECORDS` so a huge org can't pull an unbounded set into the browser.
 */
const fetchOrgCaseRecords = async (
  organizationId: string,
  filters: OrgExportFilters,
  onProgress?: (progress: ExportProgress) => void,
): Promise<CaseExportData[]> => {
  const records: CaseExportData[] = [];
  let startAfterId: string | undefined = undefined;

  for (;;) {
    const response = await caseRecordService.findOrganizationCaseRecords({
      organization_id: organizationId,
      ...filters,
      limit: EXPORT_PAGE_SIZE,
      start_after_id: startAfterId,
    });

    const page = (response.case_records ?? []) as CaseExportData[];
    records.push(...page);

    onProgress?.({
      status: 'fetching',
      current: records.length,
      total: 0,
      message: `Fetching case records (${records.length} so far)...`,
    });

    // Safety cap — stop and warn rather than pulling an unbounded set.
    if (records.length >= MAX_EXPORT_RECORDS) {
      onProgress?.({
        status: 'fetching',
        current: MAX_EXPORT_RECORDS,
        total: MAX_EXPORT_RECORDS,
        message: `Export capped at ${MAX_EXPORT_RECORDS} cases; narrow your filters to export more.`,
      });
      return records.slice(0, MAX_EXPORT_RECORDS);
    }

    // A partial page means the end on both the standard and Atlas paths.
    if (page.length < EXPORT_PAGE_SIZE) {
      return records;
    }

    // Advance the cursor: Atlas keyset token wins, else the last record's id.
    const nextCursor = response.next_page_token || page[page.length - 1]?.id;
    if (!nextCursor) {
      return records;
    }
    startAfterId = nextCursor;
  }
};

/**
 * Resolves the export's schema columns for the org-wide (Tier-2) export.
 *
 * The Tier-2 list shows PQC cases whose input_data was captured on the
 * `aepqc_preprocessing` workflow — the intake form the create-case page renders.
 * That workflow shares the PQC nomos schema module (and field IDs), so a single
 * representative `aepqc_preprocessing` workflow's schema defines the columns for
 * every case, cross-program. We list those workflows org-wide (one per accessible
 * program), take any one, and fetch its schema.
 *
 * Columns returned:
 * - `inputFields`: all input fields (file/array/object dropped — they don't
 *   serialize to a flat cell), with `case_type` guaranteed (appended if the schema
 *   omits the metadata group).
 * - `outputFields`: ONLY the capture-at-create `ack_id` (Acknowledgement ID) —
 *   deliberately not the rest of the output schema.
 *
 * Both lists fall back to the guaranteed fields on any failure so the export always
 * includes Case Type + Acknowledgement ID alongside the fixed case columns.
 */
const fetchAepqcExportSchemaFields = async (
  organizationId: string,
): Promise<{ inputFields: UIFieldInfo[]; outputFields: UIFieldInfo[] }> => {
  // Guaranteed columns used when the schema can't be loaded or omits them.
  const caseTypeFallback: UIFieldInfo = {
    id: CASE_TYPE_FIELD_ID,
    label: 'Case Type',
    groupLabel: 'Metadata',
  };
  const ackIdFallback: UIFieldInfo = { id: ACK_ID_FIELD_ID, label: 'Acknowledgement ID' };

  try {
    const wfResponse = await workflowService.listOrganizationWorkflows({
      organization_id: organizationId,
      workflow_type: AEPQC_PREPROCESSING_WORKFLOW_TYPE,
    });
    const workflow = (wfResponse.workflows ?? []).find(w => !!w.id && !!w.program_id);
    if (!workflow?.id || !workflow.program_id) {
      console.warn('No accessible aepqc_preprocessing workflow found for export schema');
      return { inputFields: [caseTypeFallback], outputFields: [ackIdFallback] };
    }

    const schemaResponse = await schemaService.getSchema({
      organization_id: organizationId,
      program_id: workflow.program_id,
      workflow_id: workflow.id,
    });

    const inputFields = extractFieldsFromUISchema(schemaResponse?.input as UISchema).filter(
      field => !field.type || !NON_FLAT_FIELD_TYPES.has(field.type),
    );
    // Guarantee Case Type — prefer the schema's own field (keeps its AE/PQC options
    // for label resolution); append a plain fallback only if the schema omits it.
    if (!inputFields.some(field => field.id === CASE_TYPE_FIELD_ID)) {
      inputFields.push(caseTypeFallback);
    }

    // Surface only the Acknowledgement ID output field, not the full output schema.
    const ackIdField =
      extractFieldsFromUISchema(schemaResponse?.output as UISchema).find(
        field => field.id === ACK_ID_FIELD_ID,
      ) ?? ackIdFallback;

    return { inputFields, outputFields: [ackIdField] };
  } catch (error) {
    console.warn('Failed to load aepqc_preprocessing schema for export:', error);
    return { inputFields: [caseTypeFallback], outputFields: [ackIdFallback] };
  }
};

/** Cases grouped by their manufacturer (PQC workflow), in first-seen order. */
interface ManufacturerCaseGroup {
  workflowId: string;
  programId: string;
  cases: CaseExportData[];
}

/** A manufacturer workflow's full export columns, resolved from its schema. */
interface ManufacturerSchemaFields {
  inputFields: UIFieldInfo[];
  outputFields: UIFieldInfo[];
  outputGroups: string[];
}

const groupCasesByWorkflow = (caseRecords: CaseExportData[]): ManufacturerCaseGroup[] => {
  const groups = new Map<string, ManufacturerCaseGroup>();
  for (const record of caseRecords) {
    const workflowId = record.workflow_id ?? '';
    const group = groups.get(workflowId);
    if (group) {
      group.cases.push(record);
    } else {
      groups.set(workflowId, {
        workflowId,
        programId: record.program_id ?? '',
        cases: [record],
      });
    }
  }
  return [...groups.values()];
};

/** Pause between successive downloads so browsers don't drop any of them. */
const MULTI_FILE_DOWNLOAD_DELAY_MS = 200;

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * "All columns" (per-manufacturer) org export: groups the already-fetched cases
 * by manufacturer (workflow_id), fetches each workflow's FULL input + output
 * schema, and downloads one file per manufacturer. Each file mirrors the
 * per-program case-list export for that workflow (full schema columns +
 * section-summary columns; no calls/eligibility/dispositions) — and has no
 * Manufacturer column, since the filename carries the manufacturer.
 *
 * A manufacturer whose schema can't be loaded produces NO file and is returned
 * in `failedManufacturers` for the caller to surface; when every schema fails,
 * throws OrgExportSchemaError naming them all.
 */
const exportOrgCasesPerManufacturer = async (
  organizationId: string,
  caseRecords: CaseExportData[],
  userEmailMap: Map<string, string>,
  options: ExportRequest['options'],
  workflowNameById?: Map<string, string>,
  onProgress?: (progress: ExportProgress) => void,
): Promise<OrgExportResult> => {
  const groups = groupCasesByWorkflow(caseRecords);
  const manufacturerName = (workflowId: string): string =>
    workflowNameById?.get(workflowId) || workflowId || 'Unknown manufacturer';

  onProgress?.({
    status: 'processing',
    current: 0,
    total: groups.length,
    message: `Fetching schemas for ${groups.length} manufacturer(s)...`,
  });

  // Resolve every group's schema up front so failures surface before any download.
  const resolved = await Promise.all(
    groups.map(
      async (
        group,
      ): Promise<{ group: ManufacturerCaseGroup; schema: ManufacturerSchemaFields | null }> => {
        if (!group.workflowId || !group.programId) {
          return { group, schema: null };
        }
        try {
          const schemaResponse = await schemaService.getSchema({
            organization_id: organizationId,
            program_id: group.programId,
            workflow_id: group.workflowId,
          });
          const inputFields = extractFieldsFromUISchema(schemaResponse?.input as UISchema);
          const outputFields = extractFieldsFromUISchema(schemaResponse?.output as UISchema);
          return {
            group,
            schema: {
              inputFields,
              outputFields,
              outputGroups: getUniqueOutputGroups(outputFields),
            },
          };
        } catch (error) {
          // Never log case data — the workflow id only.
          console.warn(`Failed to fetch schema for workflow ${group.workflowId}:`, error);
          return { group, schema: null };
        }
      },
    ),
  );

  const failedManufacturers = resolved
    .filter(entry => entry.schema === null)
    .map(entry => manufacturerName(entry.group.workflowId));
  const exportable = resolved.filter(
    (entry): entry is { group: ManufacturerCaseGroup; schema: ManufacturerSchemaFields } =>
      entry.schema !== null,
  );

  if (exportable.length === 0) {
    throw new OrgExportSchemaError(`Couldn't load columns for: ${failedManufacturers.join(', ')}`);
  }

  // One shared timestamp so the files sort together (same format as exportData's default).
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

  let files = 0;
  for (const { group, schema } of exportable) {
    const exportConfig = prepareExportConfig(
      group.cases,
      schema.inputFields,
      schema.outputFields,
      schema.outputGroups,
      new Map(), // no summaries (the section-summary columns still match the per-program layout)
      false, // no eligibility checks
      'none', // no calls
      userEmailMap,
      undefined, // no dispositions (PQC has none)
      true, // isPqc — renames "Completed At" → "Submitted At"
    );

    if (files > 0) {
      await delay(MULTI_FILE_DOWNLOAD_DELAY_MS);
    }
    files += 1;
    onProgress?.({
      status: 'generating',
      current: files,
      total: exportable.length,
      message: `Downloading file ${files} of ${exportable.length}...`,
    });
    exportData(exportConfig, {
      ...options,
      filename: `cases_${sanitizeFilenamePart(manufacturerName(group.workflowId))}_${timestamp}`,
    });
  }

  onProgress?.({
    status: 'complete',
    current: files,
    total: exportable.length,
    message: 'Export completed successfully',
  });

  return { files, failedManufacturers };
};

/**
 * Org-wide (Tier-2) case export. Fetches all cases matching the current filters
 * (identically in both modes), then either:
 * - 'common' (default): one file — fixed case columns + the create-case
 *   (aepqc_preprocessing) INPUT schema fields + Acknowledgement ID; or
 * - 'all': one file per manufacturer, each with that workflow's full schema
 *   (see exportOrgCasesPerManufacturer).
 * Triggers client-side CSV/XLSX downloads and reports what was produced.
 */
export const exportOrgCaseRecords = async (
  request: ExportOrgCaseRecordsRequest,
): Promise<OrgExportResult> => {
  const { organizationId, filters, options, onProgress, workflowNameById } = request;

  try {
    onProgress?.({ status: 'fetching', current: 0, total: 0, message: 'Starting export...' });

    // 1. Fetch all matching cases (org-wide, all pages) — identical in both modes.
    const caseRecords = await fetchOrgCaseRecords(organizationId, filters, onProgress);
    if (caseRecords.length === 0) {
      throw new Error('No case records found with the specified filters');
    }

    // 2. Resolve created_by ids → emails (same as the per-program export).
    const userEmailMap = await fetchUserEmails(caseRecords);

    // 'all' mode: one file per manufacturer, columns from each workflow's own schema.
    if (request.columnsMode === 'all') {
      return await exportOrgCasesPerManufacturer(
        organizationId,
        caseRecords,
        userEmailMap,
        options,
        workflowNameById,
        onProgress,
      );
    }

    // 3. Schema columns — the create-case (aepqc_preprocessing) intake input fields
    //    (with Case Type guaranteed) plus the single Acknowledgement ID output field.
    const { inputFields, outputFields } = await fetchAepqcExportSchemaFields(organizationId);

    // 4. Build config — input fields + ack_id output only (no summaries/calls/
    //    eligibility/disposition columns) — and download.
    onProgress?.({
      status: 'processing',
      current: 0,
      total: caseRecords.length,
      message: 'Processing data for export...',
    });
    const exportConfig = withManufacturerColumn(
      prepareExportConfig(
        caseRecords,
        inputFields,
        outputFields, // only the Acknowledgement ID output field
        [], // no summary groups
        new Map(), // no summaries
        false, // no eligibility checks
        'none', // no calls
        userEmailMap,
        undefined, // no dispositions (PQC has none)
        true, // isPqc — renames "Completed At" → "Submitted At"
      ),
      caseRecords,
      workflowNameById,
    );

    onProgress?.({
      status: 'generating',
      current: caseRecords.length,
      total: caseRecords.length,
      message: `Generating ${options.format.toUpperCase()} file...`,
    });
    exportData(exportConfig, options);

    onProgress?.({
      status: 'complete',
      current: caseRecords.length,
      total: caseRecords.length,
      message: 'Export completed successfully',
    });
    return { files: 1, failedManufacturers: [] };
  } catch (error) {
    onProgress?.({
      status: 'error',
      current: 0,
      total: 0,
      message: error instanceof Error ? error.message : 'Export failed',
    });
    throw error;
  }
};

// ── Campaign re-upload export ──────────────────────────────────────────────────
// Exports cases in the campaign FILE format (the one the bulk-create upload
// parses): headers are the flattened schema field ids (`patient_dob`,
// `j_codes.code`) and repeated values are `|`-joined — NOT the human-readable
// reporting export above, whose label headers a campaign upload rejects.

export interface ExportCasesForCampaignRequest {
  organizationId: string;
  programId: string;
  workflowId: string;
  caseRecordIds: string[];
  /** Filename without extension; the format's extension is appended. */
  filename: string;
  /** Output format; defaults to csv. Both carry the same campaign columns. */
  format?: ExportFormat;
}

/**
 * Downloads the given cases as a campaign-uploadable file: one column per
 * flattened input-schema leaf, one row per case, values from the case's
 * input_data. The produced file round-trips through the campaign upload parser.
 */
export const exportCasesForCampaign = async (
  request: ExportCasesForCampaignRequest,
): Promise<void> => {
  const schemaResponse = await schemaService.getSchema({
    organization_id: request.organizationId,
    program_id: request.programId,
    workflow_id: request.workflowId,
  });

  const groups = (schemaResponse?.input as UISchema | undefined)?.groups;
  if (!groups?.length) {
    throw new Error('Workflow has no input schema defined');
  }

  const columns = flattenSchemaFields(parseFieldGroups([...groups]).flatMap(group => group.fields));

  const caseRecords = await fetchCaseRecordsByProgram(
    { organizationId: request.organizationId, programId: request.programId },
    { workflow_id: request.workflowId, case_record_ids: request.caseRecordIds },
  );
  if (caseRecords.length === 0) {
    throw new Error('No case records found');
  }

  const rows = caseRecords.map(caseRecord =>
    buildCampaignRow(columns, caseRecord.input_data?.data as Record<string, unknown> | undefined),
  );

  exportData(
    {
      headers: columns.map(column => ({ name: column.id, label: column.id })),
      rows,
    },
    { format: request.format ?? 'csv', filename: request.filename, sheetName: 'Campaign' },
  );
};
